import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { usePlacedLots } from '@/hooks/useStorage'
import { useLocationText } from '@/hooks/useStoragePlaces'

/**
 * "Where is lot X" — pick a stored lot and jump to the slot holding it.
 *
 * A dropdown of what is actually in storage rather than a search box: the
 * unlabelled input that was here read as a filter on the panel above it, and
 * typing a lot number you do not have in front of you is not how anyone finds
 * stock. Choosing from what exists is.
 *
 * Only LOCATED stock appears — a lot with no slot has no position to jump to,
 * and lives in the put-away queue instead.
 */
export function LotLocator({
  onLocated,
}: {
  /** The position's node id plus its path, so the caller can switch roots. */
  onLocated: (nodeId: string, path: string) => void
}) {
  const locate = useLocationText()
  const placed = usePlacedLots()
  const lots = placed.data ?? []

  return (
    <div className='space-y-1'>
      <Label>Jump to a stored lot</Label>
      <Select
        value=''
        disabled={placed.isLoading || lots.length === 0}
        onValueChange={(lotId) => {
          const hit = lots.find((l) => l.lotId === lotId)
          if (hit) onLocated(hit.nodeId, hit.path)
        }}
      >
        <SelectTrigger>
          <SelectValue
            placeholder={
              placed.isLoading
                ? 'Loading…'
                : lots.length === 0
                  ? 'Nothing is in storage yet'
                  : `Choose from ${lots.length} stored…`
            }
          />
        </SelectTrigger>
        <SelectContent>
          {lots.map((lot) => (
            <SelectItem key={lot.lotId} value={lot.lotId}>
              {lot.lotNumber} · {lot.materialCode} · {locate(lot.path)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {placed.error && (
        <p className='text-xs text-destructive'>{String(placed.error.message)}</p>
      )}
    </div>
  )
}
